// =============================================================================
// rank-admin-client.http.ts — Adaptateur HTTP RÉEL du RankAdminClient (S17-bis / D2).
// =============================================================================
// Câble la SPA admin contre les endpoints GELÉS D1 du mode Rang (PositioningAdminController). Substitué
// derrière le MÊME DI seam (RankAdminClientProvider) : écrans/hooks consomment l'interface RankAdminClient,
// l'impl est substituée sans toucher l'UI (stub-first ; cet adaptateur câble le réseau).
//
// AUTORITÉ = API (NestJS, AdminSessionGuard, RBAC AdminFull, composition ACL, upsert/idempotence, relais
// figement). Cet adaptateur ne porte AUCUNE règle métier : il sérialise l'intention, parse la réponse,
// mappe l'erreur (message serveur tel quel).
//
// SÉCURITÉ (RULE_REF_006, parité campaign-client.http.ts) : le secret de session admin (cookie httpOnly)
// n'est JAMAIS lu ni fabriqué ici ; il est transmis par credentials: 'include'. Le GET (lecture du tableau)
// n'exige AUCUN en-tête CSRF ; les MUTATIONS (POST poser/retirer/figer) ré-émettent le cookie NON-httpOnly
// `admin_csrf` en en-tête `x-csrf-token` (double-submit). Sans cet en-tête, l'écriture admin est 403
// (fail-closed côté serveur — jamais d'écriture). L'auteur (byAdminId / requestedBy) est server-set
// (session), JAMAIS porté par le body. Le campaignId est la ressource d'URL.

import {
  RankAdminApiError,
  type AssignRankBodyDto,
  type AssignRankResponseDto,
  type CampaignAuditResponseDto,
  type ExportJournalResponseDto,
  type FreezePanelResponseDto,
  type RankAdminClient,
  type RankClientEvolutionResponseDto,
  type RankFreezeVersionRow,
  type RankWorkingTableDto,
  type RecordExportBodyDto,
  type RecordExportResponseDto,
  type RemoveRankBodyDto,
  type RemoveRankResponseDto,
} from '@/api/rank-admin-client';

/** Forme attendue d'un corps d'erreur structuré (NestJS HttpException). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly message?: string;
}

/** Nom du cookie CSRF NON-httpOnly posé au login admin (double-submit, cf. API CSRF_COOKIE). */
const CSRF_COOKIE_NAME = 'admin_csrf';

/**
 * Lit la valeur du cookie CSRF `admin_csrf` (NON-httpOnly, lisible par le SPA même origine). `undefined`
 * si absent (pas de session, ou contexte sans document) — l'appel mutant partira alors sans en-tête et
 * l'API répondra 403 (fail-closed côté serveur, jamais d'écriture).
 */
function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/**
 * Adaptateur HTTP réel : `fetch` contre l'API admin Rang. `baseUrl` par défaut vide (chemins relatifs :
 * SPA et API partagent l'origine, ou un reverse proxy route `/admin/*`). En test, on injecte un baseUrl
 * ou on mocke `fetch`.
 */
export class HttpRankAdminClient implements RankAdminClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  /**
   * Exécute une requête et renvoie le JSON typé, ou lève une RankAdminApiError (message serveur tel quel).
   * CSRF DOUBLE-SUBMIT sur les méthodes MUTANTES : l'en-tête `x-csrf-token` = valeur du cookie NON-httpOnly
   * `admin_csrf` (parité campaign-client.http.ts). Le secret de session (httpOnly) n'est JAMAIS lu ici.
   */
  private async request<T>(path: string, init: RequestInit): Promise<T> {
    const method = (init.method ?? 'GET').toUpperCase();
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (method !== 'GET' && method !== 'HEAD') {
      const csrf = readCsrfToken();
      if (csrf !== undefined) {
        headers['x-csrf-token'] = csrf;
      }
    }
    const response = await fetch(`${this.baseUrl}${path}`, {
      // Session admin = cookie httpOnly : transmis avec credentials: 'include'.
      credentials: 'include',
      ...init,
      headers: { ...headers, ...init.headers },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as T;
  }

  /** Convertit une réponse non-OK en erreur applicative typée (message serveur tel quel). */
  private async toApiError(response: Response): Promise<RankAdminApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur le tableau de travail Rang.`;
    return new RankAdminApiError(message, response.status);
  }

  public getRankWorkingTable(
    campaignId: string,
  ): Promise<RankWorkingTableDto> {
    return this.request<RankWorkingTableDto>(
      `/admin/positioning/${encodeURIComponent(campaignId)}/rank-working-table`,
      { method: 'GET' },
    );
  }

  public getRankClientEvolution(
    campaignIds: readonly string[],
  ): Promise<RankClientEvolutionResponseDto> {
    // Query répétée `campaignId` (parité analysis-comparison). Sans campagne ⇒ appel avec query vide
    // (l'API renvoie `{ campaigns: [] }`). GET (lecture) : aucun en-tête CSRF.
    const query = campaignIds
      .map((id) => `campaignId=${encodeURIComponent(id)}`)
      .join('&');
    const suffix = query === '' ? '' : `?${query}`;
    return this.request<RankClientEvolutionResponseDto>(
      `/admin/positioning/rank-client-evolution${suffix}`,
      { method: 'GET' },
    );
  }

  public assignRank(
    campaignId: string,
    body: AssignRankBodyDto,
  ): Promise<AssignRankResponseDto> {
    return this.request<AssignRankResponseDto>(
      `/admin/positioning/${encodeURIComponent(campaignId)}/rank`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  public removeRank(
    campaignId: string,
    body: RemoveRankBodyDto,
  ): Promise<RemoveRankResponseDto> {
    return this.request<RemoveRankResponseDto>(
      `/admin/positioning/${encodeURIComponent(campaignId)}/rank/remove`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  public freezePanel(campaignId: string): Promise<FreezePanelResponseDto> {
    // Corps VIDE (le figement cible la campagne par son id d'URL + l'admin authentifié = server-set).
    return this.request<FreezePanelResponseDto>(
      `/admin/positioning/${encodeURIComponent(campaignId)}/panel/freeze`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  public listFreezes(
    campaignId: string,
  ): Promise<readonly RankFreezeVersionRow[]> {
    // GET (lecture) : aucun en-tête CSRF.
    return this.request<readonly RankFreezeVersionRow[]>(
      `/admin/positioning/${encodeURIComponent(campaignId)}/freezes`,
      { method: 'GET' },
    );
  }

  public recordExport(
    campaignId: string,
    body: RecordExportBodyDto,
  ): Promise<RecordExportResponseDto> {
    // MUTATION : double-submit CSRF (x-csrf-token) posé par request() pour les méthodes non-GET.
    return this.request<RecordExportResponseDto>(
      `/admin/positioning/${encodeURIComponent(campaignId)}/export/record`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  public getExportJournal(
    campaignId: string,
  ): Promise<ExportJournalResponseDto> {
    // GET (lecture) : aucun en-tête CSRF.
    return this.request<ExportJournalResponseDto>(
      `/admin/positioning/${encodeURIComponent(campaignId)}/export-journal`,
      { method: 'GET' },
    );
  }

  public getCampaignAudit(
    campaignId: string,
  ): Promise<CampaignAuditResponseDto> {
    // LANE-E-CAMPAIGN-AUDIT : page d'audit campagne (base /admin/campaigns, NON /admin/positioning —
    // controller net-new CampaignAuditAdminController). GET (lecture) : aucun en-tête CSRF.
    return this.request<CampaignAuditResponseDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/audit`,
      { method: 'GET' },
    );
  }
}
